import React from 'react';
import Icon from '../../../components/AppIcon';

const ResultsDisplay = ({ results, currentLanguage }) => {
  const translations = {
    en: {
      resultsTitle: "Your Money Duration Results",
      runOutDate: "Money Runs Out On",
      daysRemaining: "Days Remaining",
      monthsRemaining: "Months Remaining",
      statusSafe: "SAFE",
      statusWarning: "WARNING",
      statusCritical: "CRITICAL",
      safeMessage: "You have sufficient savings for over 6 months",
      warningMessage: "Your savings will last 3-6 months",
      criticalMessage: "Your savings will last less than 3 months"
    },
    es: {
      resultsTitle: "Resultados de Duración de su Dinero",
      runOutDate: "El Dinero se Agota el",
      daysRemaining: "Días Restantes",
      monthsRemaining: "Meses Restantes",
      statusSafe: "SEGURO",
      statusWarning: "ADVERTENCIA",
      statusCritical: "CRÍTICO",
      safeMessage: "Tiene ahorros suficientes para más de 6 meses",
      warningMessage: "Sus ahorros durarán de 3 a 6 meses",
      criticalMessage: "Sus ahorros durarán menos de 3 meses"
    },
    pt: {
      resultsTitle: "Resultados da Duração do seu Dinheiro",
      runOutDate: "O Dinheiro Acaba em",
      daysRemaining: "Dias Restantes",
      monthsRemaining: "Meses Restantes",
      statusSafe: "SEGURO",
      statusWarning: "AVISO",
      statusCritical: "CRÍTICO",
      safeMessage: "Você tem poupança suficiente para mais de 6 meses",
      warningMessage: "Sua poupança durará de 3 a 6 meses",
      criticalMessage: "Sua poupança durará menos de 3 meses"
    },
    fr: {
      resultsTitle: "Résultats de la Durée de votre Argent",
      runOutDate: "L\'Argent s\'Épuise le",
      daysRemaining: "Jours Restants",
      monthsRemaining: "Mois Restants",
      statusSafe: "SÛR",
      statusWarning: "ATTENTION",
      statusCritical: "CRITIQUE",
      safeMessage: "Vous avez suffisamment d\'épargne pour plus de 6 mois",
      warningMessage: "Votre épargne durera de 3 à 6 mois",
      criticalMessage: "Votre épargne durera moins de 3 mois"
    },
    de: {
      resultsTitle: "Ihre Geldlaufzeit-Ergebnisse",
      runOutDate: "Geld Läuft Aus am",
      daysRemaining: "Verbleibende Tage",
      monthsRemaining: "Verbleibende Monate",
      statusSafe: "SICHER",
      statusWarning: "WARNUNG",
      statusCritical: "KRITISCH",
      safeMessage: "Sie haben ausreichend Ersparnisse für über 6 Monate",
      warningMessage: "Ihre Ersparnisse reichen für 3-6 Monate",
      criticalMessage: "Ihre Ersparnisse reichen für weniger als 3 Monate"
    }
  };

  const t = translations?.[currentLanguage] || translations?.en;

  const getStatusConfig = () => {
    if (results?.months > 6) {
      return {
        status: t?.statusSafe,
        message: t?.safeMessage,
        bgColor: 'bg-success/10',
        textColor: 'text-success',
        borderColor: 'border-success',
        icon: 'CheckCircle2'
      };
    } else if (results?.months >= 3) {
      return {
        status: t?.statusWarning,
        message: t?.warningMessage,
        bgColor: 'bg-warning/10',
        textColor: 'text-warning',
        borderColor: 'border-warning',
        icon: 'AlertTriangle'
      };
    } else {
      return {
        status: t?.statusCritical,
        message: t?.criticalMessage,
        bgColor: 'bg-error/10',
        textColor: 'text-error',
        borderColor: 'border-error',
        icon: 'AlertCircle'
      };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className="w-full max-w-2xl mx-auto mt-8 md:mt-10 lg:mt-12">
      <div className="bg-card rounded-lg shadow-elevation-3 p-6 md:p-8 lg:p-10 border border-border">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-6 md:mb-8">
          {t?.resultsTitle}
        </h2>

        <div className={`${statusConfig?.bgColor} ${statusConfig?.borderColor} border-2 rounded-lg p-4 md:p-6 mb-6 md:mb-8`}>
          <div className="flex items-center gap-3 mb-3">
            <Icon name={statusConfig?.icon} size={24} color={`var(--color-${statusConfig?.status === t?.statusSafe ? 'success' : statusConfig?.status === t?.statusWarning ? 'warning' : 'error'})`} />
            <span className={`${statusConfig?.textColor} text-lg md:text-xl lg:text-2xl font-heading font-semibold`}>
              {statusConfig?.status}
            </span>
          </div>
          <p className={`${statusConfig?.textColor} text-sm md:text-base`}>
            {statusConfig?.message}
          </p>
        </div>

        <div className="space-y-4 md:space-y-6">
          <div className="bg-muted rounded-lg p-4 md:p-5 lg:p-6">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Calendar" size={20} color="var(--color-foreground)" />
              <p className="text-sm md:text-base text-muted-foreground font-medium">
                {t?.runOutDate}
              </p>
            </div>
            <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
              {results?.runOutDate}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-muted rounded-lg p-4 md:p-5 lg:p-6">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Clock" size={20} color="var(--color-foreground)" />
                <p className="text-sm md:text-base text-muted-foreground font-medium">
                  {t?.daysRemaining}
                </p>
              </div>
              <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
                {results?.days}
              </p>
            </div>

            <div className="bg-muted rounded-lg p-4 md:p-5 lg:p-6">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="TrendingUp" size={20} color="var(--color-foreground)" />
                <p className="text-sm md:text-base text-muted-foreground font-medium">
                  {t?.monthsRemaining}
                </p>
              </div>
              <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
                {results?.months}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;